import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Crown, Pencil, Trash2 } from "lucide-react";
import { getClients, deleteClient, getTipos } from "../api";
import { AuthContext } from "../AuthContext";

export default function ListClients() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [tipos, setTipos] = useState([]);

  const carregarClientes = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getClients(user.token);
      setClients(data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      alert("Erro ao carregar lista de clientes.");
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este cliente?")) return;
    try {
      await deleteClient(user.token, id);
      setClients((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Erro ao excluir cliente:", error);
      alert("Não foi possível excluir o cliente.");
    }
  };

  const fetchTipos = useCallback(async () => {
    try {
      const data = await getTipos(user.token);
      setTipos(data);
    } catch (error) {
      alert("Erro ao carregar tipos.");
    }
  }, [user.token]);
  
  useEffect(() => {
    carregarClientes();
    fetchTipos();
  }, [carregarClientes, fetchTipos]);

  const clientesFiltrados = useMemo(() => {
    const termoCNPJ = searchTerm.replace(/\D/g, "");
    const termoNome = searchTerm.toLowerCase();

    return clients
      .filter((c) => (id ? String(c.id) === String(id) : true))
      .filter((c) => {
        if (!searchTerm.trim()) return true;

        const nomeMatch = c.razao_social?.toLowerCase().includes(termoNome);
        const fantasiaMatch = c.nome_fantasia?.toLowerCase().includes(termoNome);

        const cnpjMatch =
          termoCNPJ.length > 0 &&
          c.cnpj?.replace(/\D/g, "").includes(termoCNPJ);

        return nomeMatch || fantasiaMatch || cnpjMatch;
      });
  }, [searchTerm, clients, id]);

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-grid">
          <div className="user-card">
            <h3><Crown size={20} /> Clientes</h3>

            {id && (
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "10px 14px",
                  background: "rgba(59, 130, 246, 0.1)",
                  color: "#2563eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Exibindo cliente ID: {id}
                <button
                  onClick={() => navigate("/listclients")}
                  style={{
                    marginLeft: "12px",
                    background: "transparent",
                    border: "none",
                    color: "#2563eb",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Mostrar todos
                </button>
              </div>
            )}

            {/* Campo de busca */}
            <div className="form-group">
              <input
                type="text"
                placeholder="Pesquisar por nome ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* INFO */}
            <div style={{ marginBottom: "10px", fontSize: "12px", color: "#666" }}>
              Total: {clients.length} | Filtrados: {clientesFiltrados.length}
            </div>

            {loading ? (
              <div className="center-content">
                <div className="spinner"></div>
                <p>Carregando clientes...</p>
              </div>
            ) : clientesFiltrados.length === 0 ? (
              <p>Nenhum cliente encontrado.</p>
            ) : (
              <div className="table-wrapper">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>CNPJ</th>
                      <th>Razão Social</th>
                      <th>Nome Fantasia</th>
                      <th>Tipo</th>
                      <th colSpan="2">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesFiltrados.map((client) => (
                      <tr key={client.id}>
                        <td>{client.cnpj}</td>
                        <td>{client.razao_social}</td>
                        <td>{client.nome_fantasia}</td>
                        <td>
                          {tipos.find((t) => t.id === client.tipo_id)?.descricao || "-"}
                        </td>
                        <td className="actions">
                        <button
                          className="btn-table btn-small"
                          onClick={() => navigate(`/addclients/${client.id}`)}
                        >
                          <Pencil size={18} />
                        </button>
                        </td>
                        <td>
                          <button
                            className="btn-danger btn-small"
                            onClick={() => handleDelete(client.id)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}